import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import zigbeeConfig from '../config/zigbee';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AuthUser {
    id: number;
    username: string;
    userLevel: 'read' | 'admin' | 'superuser';
    timezone: string;
}

interface AuthContextValue {
    user: AuthUser | null;
    token: string | null;
    requestOtp: (email: string) => Promise<void>;
    verifyOtp: (email: string, code: string) => Promise<void>;
    logout: () => void;
    requestSuperuserOtp: (email: string) => Promise<void>;
    verifySuperuserOtp: (email: string, code: string) => Promise<void>;
    selectTenant: (tenantId: string) => void;
    activeTenantId: string;
    isAdmin: boolean;
    isSuperuser: boolean;
    isReadOnly: boolean;
    isHandoffPending: boolean;
    timezone: string;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue>({
    user: null,
    token: null,
    requestOtp: async () => {},
    verifyOtp: async () => {},
    logout: () => {},
    requestSuperuserOtp: async () => {},
    verifySuperuserOtp: async () => {},
    selectTenant: () => {},
    activeTenantId: zigbeeConfig.tenantId,
    isAdmin: false,
    isSuperuser: false,
    isReadOnly: false,
    isHandoffPending: false,
    timezone: 'Europe/Stockholm',
});

const TOKEN_KEY = 'iot_token';
const USER_KEY  = 'iot_user';
const SUPERUSER_TENANT_KEY = 'iot_superuser_tenant';

/**
 * Senast inloggade e-postadress, så att inloggningsrutan är förifylld när koden
 * gått ut. Bara adressen sparas, aldrig koden. Rensas vid aktiv utloggning men
 * inte när token bara löper ut – det är ju då den sparar ett steg.
 */
export const LOGIN_EMAIL_KEY = 'iot_login_email';
export const readSavedLoginEmail = (): string => {
    try { return localStorage.getItem(LOGIN_EMAIL_KEY) ?? ''; } catch { return ''; }
};
export const saveLoginEmail = (email: string): void => {
    try { localStorage.setItem(LOGIN_EMAIL_KEY, email); } catch { /* privat läge m.m. */ }
};
export const clearSavedLoginEmail = (): void => {
    try { localStorage.removeItem(LOGIN_EMAIL_KEY); } catch { /* ignorera */ }
};

/** Decode a JWT payload without verifying the signature (client-side only). */
function parseJwtPayload(token: string): Record<string, any> | null {
    try {
        const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(b64));
    } catch { return null; }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
    const [isHandoffPending, setIsHandoffPending] = useState<boolean>(
        () => new URLSearchParams(window.location.search).has('suCode')
    );
    const [activeTenantId, setActiveTenantId] = useState<string>(
        () => localStorage.getItem(SUPERUSER_TENANT_KEY) ?? zigbeeConfig.tenantId
    );
    const [user, setUser]   = useState<AuthUser | null>(() => {
        const raw = localStorage.getItem(USER_KEY);
        try { if (raw) return JSON.parse(raw); } catch {}
        // No stored user — reconstruct from JWT (e.g. after cross-tenant redirect)
        const storedToken = localStorage.getItem(TOKEN_KEY);
        if (storedToken) {
            const payload = parseJwtPayload(storedToken);
            if (payload?.id && payload?.username) {
                const authUser: AuthUser = {
                    id: payload.id,
                    username: payload.username,
                    userLevel: payload.level === 3 ? 'superuser' : payload.level === 2 ? 'admin' : 'read',
                    timezone: 'Europe/Stockholm',
                };
                localStorage.setItem(USER_KEY, JSON.stringify(authUser));
                return authUser;
            }
        }
        return null;
    });

    // Superuser cross-tenant redirect: exchange ?suCode= for the real JWT.
    // Using a query param (not URL fragment) so it appears in history — but it's
    // an opaque one-time code (not the JWT itself) and is immediately consumed and
    // removed from the URL.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const suCode = params.get('suCode');
        if (!suCode) return;
        // Remove code from URL immediately so it isn't bookmarked or shared
        params.delete('suCode');
        const newSearch = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
        axios.get(`${zigbeeConfig.apiBaseUrl}/superuser/su-redeem`, { params: { code: suCode } })
            .then(res => {
                const suToken: string = res.data.token;
                localStorage.setItem(TOKEN_KEY, suToken);
                setToken(suToken);
                const payload = parseJwtPayload(suToken);
                if (payload?.id && payload?.username) {
                    const authUser: AuthUser = {
                        id: payload.id,
                        username: payload.username,
                        userLevel: payload.level === 3 ? 'superuser' : payload.level === 2 ? 'admin' : 'read',
                        timezone: 'Europe/Stockholm',
                    };
                    localStorage.setItem(USER_KEY, JSON.stringify(authUser));
                    setUser(authUser);
                }
            })
            .catch(() => { /* code expired/invalid — user will see login screen */ })
            .finally(() => setIsHandoffPending(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep axios Authorization header in sync
    useEffect(() => {
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
            delete axios.defaults.headers.common['Authorization'];
        }
    }, [token]);

    // Restore superuser tenant selection on page load
    useEffect(() => {
        const savedTenant = localStorage.getItem(SUPERUSER_TENANT_KEY);
        if (savedTenant) {
            axios.defaults.headers.common['X-Tenant-Id'] = savedTenant;
        }
    }, []);

    const requestOtp = useCallback(async (email: string) => {
        await axios.post(`${zigbeeConfig.apiBaseUrl}/auth/otp/request`, { email });
    }, []);

    const verifyOtp = useCallback(async (email: string, code: string) => {
        const res = await axios.post(`${zigbeeConfig.apiBaseUrl}/auth/otp/verify`, { email, code });
        const { token: t, user: u } = res.data;
        setToken(t);
        setUser(u);
        localStorage.setItem(TOKEN_KEY, t);
        localStorage.setItem(USER_KEY, JSON.stringify(u));
        axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    }, []);

    const requestSuperuserOtp = useCallback(async (email: string) => {
        await axios.post(`${zigbeeConfig.apiBaseUrl}/superuser/otp/request`, { email });
    }, []);

    const verifySuperuserOtp = useCallback(async (email: string, code: string) => {
        const res = await axios.post(`${zigbeeConfig.apiBaseUrl}/superuser/otp/verify`, { email, code });
        const { token: t, user: u } = res.data;
        const fullUser: AuthUser = { ...u, timezone: 'Europe/Stockholm' };
        setToken(t);
        setUser(fullUser);
        localStorage.setItem(TOKEN_KEY, t);
        localStorage.setItem(USER_KEY, JSON.stringify(fullUser));
        axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    }, []);

    const selectTenant = useCallback((tenantId: string) => {
        axios.defaults.headers.common['X-Tenant-Id'] = tenantId;
        localStorage.setItem(SUPERUSER_TENANT_KEY, tenantId);
        setActiveTenantId(tenantId);
    }, []);

    const logout = useCallback(() => {
        setToken(null);
        setUser(null);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(SUPERUSER_TENANT_KEY);
        clearSavedLoginEmail();
        delete axios.defaults.headers.common['Authorization'];
        // Reset to default tenant derived from hostname
        axios.defaults.headers.common['X-Tenant-Id'] = zigbeeConfig.tenantId;
        setActiveTenantId(zigbeeConfig.tenantId);
    }, []);

    const isSuperuser = user?.userLevel === 'superuser';
    const isReadOnly  = user?.userLevel === 'read';
    const isAdmin     = user?.userLevel === 'admin' || isSuperuser;

    return (
        <AuthContext.Provider value={{
            user, token,
            requestOtp, verifyOtp, logout,
            requestSuperuserOtp, verifySuperuserOtp, selectTenant, activeTenantId,
            isAdmin, isSuperuser, isReadOnly, isHandoffPending,
            timezone: user?.timezone || 'Europe/Stockholm',
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
